/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Info, Trophy, Lock, Star, HardHat } from "lucide-react";
import { V3Header } from "./v3/V3Header";
import { fmtD } from "../../lib/usDate";

import { T } from "../../lib/i18n";
const scoreColor = (v) => (v == null ? "text-slate-400" : v >= 85 ? "text-emerald-600" : v >= 70 ? "text-amber-600" : "text-red-600");
const pct = (v) => (v == null ? "—" : `${v}%`);
const avg1 = (v) => (v == null ? "—" : Number(v).toFixed(1));
const starColor = (v) => (v == null ? "text-slate-400" : v >= 4.5 ? "text-emerald-600" : v >= 3.5 ? "text-amber-600" : "text-red-600");
const Stars = ({ v, tid }) => <span className="inline-flex" data-testid={tid} title={`${v}/5`}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= Math.round(v) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</span>;

function Sparkline({ weeks, testid }) {
  const vals = weeks.map((w) => w.weightedScore);
  const pts = vals.map((v, i) => ({ v, i }));
  const w = 120, h = 32, pad = 3;
  const xs = (i) => pad + (i / Math.max(1, vals.length - 1)) * (w - 2 * pad);
  const ys = (v) => h - pad - ((v ?? 0) / 100) * (h - 2 * pad);
  const line = pts.filter((p) => p.v != null).map((p, k) => `${k === 0 ? "M" : "L"}${xs(p.i)},${ys(p.v)}`).join(" ");
  return (
    <svg width={w} height={h} data-testid={testid} className="overflow-visible">
      <line x1={pad} y1={ys(85)} x2={w - pad} y2={ys(85)} stroke="#e2e8f0" strokeDasharray="2 2" />
      <path d={line} fill="none" stroke="#6366f1" strokeWidth="1.8" />
      {pts.map((p) => p.v != null && <circle key={p.i} cx={xs(p.i)} cy={ys(p.v)} r="1.8" fill="#6366f1" />)}
    </svg>
  );
}

export default function DesignReports() {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const isMgr = ["admin", "ceo", "design_manager"].includes(user?.role);
  const [mine, setMine] = useState(null);
  const [myTrend, setMyTrend] = useState(null);
  const [teamTrend, setTeamTrend] = useState(null);
  const [ratings, setRatings] = useState(null);

  useEffect(() => {
    api.get("/design/reports/monthly").then(({ data }) => setMine(data)).catch(() => {});
    api.get("/design/reports/trend?weeks=8").then(({ data }) => setMyTrend(data)).catch(() => {});
    api.get("/design/v3/handoff-ratings?days=90").then(({ data }) => setRatings(data)).catch(() => setRatings({ byOwner: [], recent: [], mine: null }));
    if (isMgr) api.get("/design/reports/team-trend?weeks=8").then(({ data }) => setTeamTrend(data)).catch(() => {});
  }, [api, isMgr]);

  const ratingOf = (owner) => (ratings?.byOwner || []).find((x) => x.ownerUsername === owner || x.owner === owner);

  const Card = ({ label, v, sub, color }) => (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${color || "text-slate-800"}`}>{v}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );

  const ranked = teamTrend?.members || [];
  const unlocked = teamTrend?.rankingUnlocked;

  return (
    <div className="space-y-4" data-testid="design-reports-page">
      <V3Header title={zh ? "绩效报表（复杂度加权）" : "Performance Reports"} sub={zh ? "个人月度绩效 · 8 周趋势 · 团队排名" : "Monthly performance · 8-week trend · team ranking"} />
      <div className="flex-wrap md:flex-nowrap flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg p-3" data-testid="design-reports-notice">
        <Info className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
        <p className="text-[12px] text-indigo-700">{zh ? "观察期：前 4 周仅统计不关联奖惩；City/Owner/第三方等待不计入个人逾期；样本 < 3 不参与排名。趋势为按周窗口计算。" : "Observation window: first 4 weeks observation-only; external waits excluded; sample<3 not ranked. Trend is per-week."}</p>
      </div>

      {/* personal */}
      {mine && (
        <div className="mb-6" data-testid="design-my-perf">
          <p className="text-sm font-semibold text-slate-700 mb-2">{zh ? "我的月度绩效" : "My Monthly Performance"} · {mine.owner}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-3">
            <Card label={zh ? "综合分" : "Score"} v={mine.weightedScore ?? "—"} color={scoreColor(mine.weightedScore)} sub={mine.rankable ? (zh ? "可排名" : "rankable") : (zh ? "样本不足" : "n<3")} />
            <Card label={zh ? "内部准时率" : "SLA"} v={pct(mine.internalSlaRate)} />
            <Card label={zh ? "质量分" : "Quality"} v={mine.quality} />
            <Card label={zh ? "一次通过率" : "1st-Pass"} v={pct(mine.firstPassQARate)} />
            <Card label={zh ? "文件合规" : "Gate"} v={pct(mine.gateCompliance)} />
            <Card label={zh ? "复杂度点" : "Points"} v={mine.completedPoints} sub={`${mine.completedTasks} ${zh ? "任务" : "tasks"}`} />
            <Card label={zh ? "返工次数" : "Rework"} v={mine.reworkTotal} sub={`${zh ? "中位" : "med"} ${mine.medianDurationDays ?? "—"}d`} />
            <Card label={zh ? "交接评分" : "Handoff ★"} v={ratings?.mine ? `${avg1(ratings.mine.avg)}★` : "—"} color={ratings?.mine ? starColor(ratings.mine.avg) : undefined} sub={ratings?.mine ? `${ratings.mine.count} ${zh ? "次 · 近 90 天" : "ratings / 90d"}` : (zh ? "工程部尚未评分" : "no ratings")} />
          </div>
          {myTrend && (
            <div className="bg-white border border-slate-200 p-4 flex items-center gap-4 flex-wrap md:flex-nowrap rounded-xl" data-testid="design-my-trend">
              <div><p className="text-xs text-slate-500 mb-1">{zh ? "近 8 周综合分趋势" : "8-week score trend"}</p><Sparkline weeks={myTrend.weeks} testid="design-my-sparkline" /></div>
              <div className="flex gap-1.5 flex-wrap text-[10px] text-slate-400">
                {myTrend.weeks.map((w) => <span key={w.weekStart} className="px-1.5 py-0.5 bg-slate-50 rounded">{w.weekStart.slice(5)}: <b className={scoreColor(w.weightedScore)}>{w.weightedScore ?? "—"}</b></span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* team leaderboard + trend */}
      {isMgr && (
        <div className="bg-white border border-slate-200 p-4 overflow-x-auto rounded-xl" data-testid="design-team-perf">
          <div className="flex items-center justify-between mb-3 flex-wrap md:flex-nowrap">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" />{zh ? "团队排行榜（近 30 天）" : "Team Leaderboard (30d)"}</p>
            {!unlocked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 flex items-center gap-1"><Lock className="h-3 w-3" />{zh ? "观察期：排名未解锁" : "Observation — ranking locked"}</span>}
          </div>
          {ranked.length === 0 ? <p className="text-slate-400 text-sm">{zh ? "暂无数据" : "No data"}</p> : (
            <ResponsiveTable className="w-full text-sm min-w-[720px]">
              <thead><tr className="text-left text-xs text-slate-400">
                <th className="py-1 pr-3">#</th><th className="pr-3">{zh ? "成员" : "Member"}</th><th className="pr-3">{zh ? "综合分" : "Score"}</th>
                <th className="pr-3">{zh ? "质量" : "Quality"}</th><th className="pr-3">{zh ? "复杂度点" : "Points"}</th><th className="pr-3">{zh ? "交接★" : "Handoff ★"}</th><th>{zh ? "8 周趋势" : "8w Trend"}</th>
              </tr></thead>
              <tbody>
                {ranked.map((m, i) => (
                  <tr key={m.owner} className="border-t border-slate-100" data-testid={`design-perf-row-${m.owner}`}>
                    <td className="py-1.5">{unlocked && m.rankable ? (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1) : "—"}</td>
                    <td className="font-medium text-slate-700">{m.owner}{!m.rankable && <span className="ml-1 text-[9px] text-slate-400">({zh ? "样本不足" : "n<3"})</span>}</td>
                    <td className={`font-bold ${scoreColor(m.weightedScore)}`}>{m.weightedScore ?? "—"}</td>
                    <td>{m.quality}</td>
                    <td>{m.completedPoints}</td>
                    <td data-testid={`design-perf-handoff-${m.owner}`}>{ratingOf(m.owner) ? <span className={`font-bold ${starColor(ratingOf(m.owner).avg)}`}>{avg1(ratingOf(m.owner).avg)}★ <span className="text-[10px] text-slate-400 font-normal">({ratingOf(m.owner).count})</span></span> : <span className="text-slate-300">—</span>}</td>
                    <td><Sparkline weeks={m.trend} testid={`design-sparkline-${m.owner}`} /></td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          )}
          <p className="text-[10px] text-slate-400 mt-3">{zh ? "综合分 = 内部准时25% + 质量30% + 复杂度产出20% + 文件合规10% + 稳定10% + 协作5%（观察期公式，可调）。满 4 周后自动解锁排名。" : "Score formula; ranking auto-unlocks after 4 weeks."}</p>
        </div>
      )}

      {/* engineering handoff ratings */}
      {ratings && (
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="design-handoff-ratings">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><HardHat className="h-4 w-4 text-amber-600" />{zh ? "工程交接评分（近 90 天）" : "Construction Handoff Ratings (90d)"}</p>
            <span className="text-xs text-slate-500" data-testid="design-handoff-ratings-summary">{ratings.total ? (zh ? `${ratings.total} 次评分 · 平均 ${avg1(ratings.avg)}★` : `${ratings.total} ratings · avg ${avg1(ratings.avg)}★`) : (zh ? "工程部尚未对任何移交项目评分" : "No ratings yet")}</span>
            <span className="ml-auto text-[10px] text-slate-400">{zh ? "由工程 PM / 工程经理在工程项目中心「设计移交回执」打分" : "Rated by PM / eng manager on the handoff receipt"}</span>
          </div>
          {ratings.byOwner.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
              {ratings.byOwner.map((o) => (
                <div key={o.owner} className="rounded-lg border border-slate-100 p-2.5" data-testid={`design-handoff-owner-${o.ownerUsername || o.owner}`}>
                  <div className="flex-wrap md:flex-nowrap flex items-center gap-2"><span className="text-sm font-semibold text-slate-800 flex-1 truncate">{o.owner}</span><b className={`text-sm ${starColor(o.avg)}`}>{avg1(o.avg)}★</b><span className="text-[10px] text-slate-400">({o.count})</span></div>
                  <Stars v={o.avg} tid={`design-handoff-stars-${o.ownerUsername || o.owner}`} />
                  {o.latest?.comment && <p className="text-[11px] text-slate-500 italic mt-1 truncate" title={o.latest.comment}>“{T(o.latest.comment)}” · {o.latest.by}</p>}
                </div>
              ))}
            </div>
          )}
          {ratings.recent.length > 0 && (
            <div className="divide-y divide-slate-100" data-testid="design-handoff-recent">
              {ratings.recent.slice(0, 8).map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs py-1.5" data-testid={`design-handoff-recent-${r.id}`}>
                  <Stars v={r.score} /><b className={starColor(r.score)}>{r.score}/5</b>
                  <span className="text-slate-700 font-medium truncate max-w-[280px]">{r.address}</span>
                  <span className="text-slate-400">{zh ? "负责" : "Lead"} {r.owner}</span>
                  {r.comment && <span className="text-slate-500 italic truncate max-w-[320px]">“{T(r.comment)}”</span>}
                  <span className="ml-auto text-slate-400">{r.by} · {fmtD(r.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
